import { observer } from 'mobx-react-lite';
import { motion, useReducedMotion } from 'motion/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { IconButton, XStack, YStack } from '@/components/ui';
import { useAppShortcut } from '@/features/shortcuts';
import { useClickAway } from '@/hooks/useClickAway';
import { useViewportSize } from '@/hooks/useViewportSize';
import { EASE, PANEL_MORPH_S } from '@/lib/animate';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';

import { ChatPanelContent } from './context-chat-panel';
import { floating as floatingConfig } from './context-chat.config';
import { useChatDockInset, useDockMetrics } from './context-chat.hooks';
import {
  type ContextChatHost,
  ContextChatHostContext,
  mainWindowChatHost,
} from './context-chat.host';
import { contextChatStore } from './context-chat.state';
import { surfaceForSession } from './context-chat.utils';

const FLOATING_HEADROOM_PX = 180;

const NO_OVERLAY_EXPANDED = () => false;

interface ContextChatDockProps {
  host?: ContextChatHost;
  className?: string;
  maxWidthClassName?: string;
  panelClassName?: string;
  isOverlayExpanded?: () => boolean;
  leading?: React.ReactNode;
  children?: React.ReactNode;
}

export const ContextChatDock = observer(function ContextChatDock({
  host = mainWindowChatHost,
  className,
  maxWidthClassName = 'max-w-3xl',
  panelClassName,
  isOverlayExpanded = NO_OVERLAY_EXPANDED,
  leading,
  children,
}: ContextChatDockProps) {
  const { mode, activeSession } = contextChatStore;
  const dockRef = useChatDockInset(isOverlayExpanded);

  useAppShortcut(
    'contextChat.toggle',
    (e) => {
      e.preventDefault();
      if (contextChatStore.mode === 'collapsed') contextChatStore.expand();
      else contextChatStore.collapse();
    },
    { enableOnContentEditable: true, enableOnFormTags: true },
    []
  );

  const prevModeRef = useRef(mode);
  useEffect(() => {
    if (prevModeRef.current === 'collapsed' && mode !== 'collapsed') {
      trackEvent('CONTEXT_CHAT_OPENED', {
        context: surfaceForSession(contextChatStore.activeSession),
        mode,
        preferDocked: contextChatStore.preferDocked,
      });
    }
    prevModeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    if (mode !== 'floating' && mode !== 'composer') return;

    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      contextChatStore.collapse();
    };

    window.addEventListener('keydown', handler);

    return () => window.removeEventListener('keydown', handler);
  }, [mode]);

  const showChat = !!activeSession && mode !== 'docked';

  return (
    <ContextChatHostContext.Provider value={host}>
      <YStack
        ref={dockRef}
        className={cn(
          'pointer-events-none absolute bottom-6 inset-x-0 z-10 items-center gap-4',
          className
        )}
      >
        {children}
        <div
          className={cn(
            'mx-auto flex w-full items-end px-6',
            maxWidthClassName,
            !showChat && 'justify-center'
          )}
        >
          {leading}
          {showChat && <ChatDockPanel className={panelClassName} />}
        </div>
      </YStack>
    </ContextChatHostContext.Provider>
  );
});

export function DockLeadingSlot({ children }: { children: React.ReactNode }) {
  const shouldReduceMotion = useReducedMotion();
  const hidden = shouldReduceMotion
    ? { opacity: 0 }
    : { opacity: 0, width: 0, scale: 0.98, marginRight: 0 };

  return (
    <motion.div
      className="pointer-events-auto shrink-0 overflow-hidden"
      initial={hidden}
      animate={{ opacity: 1, width: 'auto', scale: 1, marginRight: 8 }}
      exit={hidden}
      transition={{
        duration: shouldReduceMotion ? 0 : PANEL_MORPH_S,
        ease: EASE.inOutCubic,
      }}
    >
      {children}
    </motion.div>
  );
}

const CARD_BORDER_PX = 2;

const ChatDockPanel = observer(function ChatDockPanel({
  className,
}: {
  className?: string;
}) {
  const shouldReduceMotion = useReducedMotion();
  const { height: viewportHeight } = useViewportSize();
  const mode = contextChatStore.mode;
  const floating = mode === 'floating';
  const resizing = contextChatStore.isResizingFloating;
  const cardRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [isMorphing, setIsMorphing] = useState(false);
  const prevModeRef = useRef(mode);

  useLayoutEffect(() => {
    if (prevModeRef.current === mode) return;
    prevModeRef.current = mode;
    const el = contentRef.current;
    if (el && mode !== 'floating') setContentHeight(el.scrollHeight);
    if (!shouldReduceMotion) setIsMorphing(true);
  }, [mode, shouldReduceMotion]);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const measure = () => {
      if (contextChatStore.mode !== 'floating') {
        setContentHeight(el.scrollHeight);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useClickAway(cardRef, () => contextChatStore.collapse(), {
    enabled: mode === 'composer' || mode === 'floating',
    touchEvent: false,
    ignoreSelector:
      '[data-radix-popper-content-wrapper],[role="menu"],[role="listbox"],[role="dialog"]',
  });

  const maxHeight = Math.min(
    floatingConfig.maxHeight,
    Math.max(floatingConfig.minHeight, viewportHeight - FLOATING_HEADROOM_PX)
  );
  const renderedHeight = Math.min(contextChatStore.floatingHeight, maxHeight);

  const height = floating ? renderedHeight : contentHeight + CARD_BORDER_PX;

  return (
    <motion.div
      ref={cardRef}
      className={cn(
        'pointer-events-auto relative flex min-w-0 flex-1 flex-col justify-end overflow-hidden border border-border-strong bg-surface',
        'transition-[box-shadow,border-radius] duration-[260ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
        floating ? 'rounded-3xl shadow-lg' : 'rounded-2xl shadow-sm',
        className
      )}
      initial={false}
      animate={{ height }}
      transition={{
        duration:
          (prevModeRef.current !== mode || isMorphing) &&
          !resizing &&
          !shouldReduceMotion
            ? PANEL_MORPH_S
            : 0,
        ease: EASE.inOutCubic,
      }}
      onAnimationComplete={() => setIsMorphing(false)}
    >
      {floating && <FloatingHeightHandle maxHeight={maxHeight} />}
      {floating && <FloatingTopBar />}
      <div
        ref={contentRef}
        className={cn(
          'flex min-h-0 flex-col justify-end',
          mode === 'floating' ? 'flex-1' : 'shrink-0',
          mode === 'collapsed' && 'h-14'
        )}
      >
        <ChatPanelContent />
      </div>
    </motion.div>
  );
});

const FLOATING_TOP_BAR_BUTTON = 'rounded-full';

const FloatingTopBar = observer(function FloatingTopBar() {
  const { canDock } = useDockMetrics();
  const session = contextChatStore.activeSession;
  const isGeneral = session?.context.type === 'general';

  return (
    <div className="flex shrink-0 items-center justify-between px-2 pt-2">
      <IconButton
        size="sm"
        variant="ghost"
        icon={contextChatStore.dockOnly ? Icons.cancel : Icons.chevronDown}
        tooltip={contextChatStore.dockOnly ? 'Close' : 'Minimize'}
        className={FLOATING_TOP_BAR_BUTTON}
        onClick={() => contextChatStore.collapse()}
      />
      <XStack className="gap-1">
        {isGeneral && (
          <IconButton
            size="sm"
            variant="ghost"
            icon={Icons.plus}
            tooltip="New chat"
            className={FLOATING_TOP_BAR_BUTTON}
            onClick={() => {
              void contextChatStore.startNewGeneralChat();
            }}
          />
        )}
        {canDock && (
          <IconButton
            size="sm"
            variant="ghost"
            icon={Icons.maximize}
            tooltip="Dock to side"
            className={FLOATING_TOP_BAR_BUTTON}
            onClick={() => {
              contextChatStore.dock();
              trackEvent('CHAT_DOCKED', {
                context: surfaceForSession(session),
              });
            }}
          />
        )}
      </XStack>
    </div>
  );
});

const FloatingHeightHandle = observer(function FloatingHeightHandle({
  maxHeight,
}: {
  maxHeight: number;
}) {
  const dragRef = useRef<{ startY: number; startHeight: number } | null>(null);

  useEffect(() => {
    return () => {
      if (!dragRef.current) return;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      contextChatStore.setResizingFloating(false);
    };
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      startY: e.clientY,
      startHeight: contextChatStore.floatingHeight,
    };
    contextChatStore.setResizingFloating(true);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const { startY, startHeight } = dragRef.current;
    const next = Math.min(maxHeight, startHeight + (startY - e.clientY));
    contextChatStore.setFloatingHeight(next);
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    dragRef.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    contextChatStore.setResizingFloating(false);
  };

  return (
    <div
      role="separator"
      aria-orientation="horizontal"
      data-resize-handle
      data-dragging={contextChatStore.isResizingFloating ? 'true' : 'false'}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      className="group absolute top-0.5 inset-x-4 h-[6px] flex items-start justify-center cursor-ns-resize z-20"
    >
      <div
        className={cn(
          'h-0 w-12 rounded-full bg-border-strongest',
          'group-hover:h-1 group-hover:opacity-100',
          'group-data-[dragging=true]:h-1 group-data-[dragging=true]:opacity-100',
          'transition-[opacity,height] will-change-[height,opacity]'
        )}
      />
    </div>
  );
});
